/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * CustomCardList
 *
 * @file CustomCardList.jsx
 * @author Mohamed yasar arabath M
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import mixins from '../../assets/styles/mixins.scss';
import CustomCard from './CustomCard';
import CustomScrollbar from '../CustomScrollbar/CustomScrollbar';
import { BadgeTemplate } from '../tabular/Tabular';

const CardListCustomCard = styled(CustomCard)({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    alignItems: 'flex-start',
    width: '100%',
    height: '100%',
    maxHeight: 'var(--custom-card-list-max-height)',
    borderRadius: '16px'
  }
});

const Body = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    width: '100%',
    position: 'relative',
    flex: 1,
    overflowY: 'auto'
  }
});

const List = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    position: 'relative',
    flex: 1,
    overflowY: 'auto'
  }
});

const ListItem = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%'
  }
});

const Title = styled(Typography)({
  '&&': {
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize20,
    lineHeight: '28px',
    fontWeight: mixins.fontWeight500,
    letterSpacing: '-0.4px',
    marginBottom: 16
  }
});

const ListText = styled(Typography)({
  '&&': {
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize12,
    lineHeight: '20px',
    fontWeight: mixins.fontWeight400,
    flex: 1,
    textAlign: 'left'
  }
});

const DefaultChipAccentWrapper = styled('div')({
  '&&': {
    '& .MuiChip-root': {
      color: mixins.accentRed
    }
  }
});

export default function CustomCardList({
  title, items, isBadgeUsed, customStyles, status, classes: customClasses = {}
}) {
  const renderListItemContent = (item) => {
    if (!isBadgeUsed) {
      return (
        <ListText>
          {item}
        </ListText>
      );
    }

    if (customStyles?.chip) {
      return (
        <BadgeTemplate
          topics={[item]}
          classes={{
            chip: customStyles.chip
          }}
          status={status}
        />
      );
    }

    return (
      <DefaultChipAccentWrapper>
        <BadgeTemplate
          topics={[item]}
          status={status}
        />
      </DefaultChipAccentWrapper>
    );
  };

  return (
    <CardListCustomCard>
      <CustomScrollbar autoHeight>
        {customClasses?.title ? (
          <Typography className={customClasses.title} variant="inherit" component="p">
            {title}
          </Typography>
        ) : (
          <Title variant="inherit" component="p">
            {title}
          </Title>
        )}

        <Body>
          <List style={{ gap: isBadgeUsed ? 8 : 2 }}>
            {items.map((item, idx) => (
              <ListItem key={idx}>
                {renderListItemContent(item)}
              </ListItem>
            ))}
          </List>
        </Body>
      </CustomScrollbar>
    </CardListCustomCard>
  );
}
CustomCardList.defaultProps = {
  isBadgeUsed: false,
  customStyles: '',
  status: '',
  classes: {}
};

CustomCardList.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
  isBadgeUsed: PropTypes.bool,
  customStyles: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      chip: PropTypes.string
    })
  ]),
  status: PropTypes.string,
  classes: PropTypes.shape({
    title: PropTypes.string
  })
};
